import type { Annotation } from "building-types/annotation/annotationTypes";
import { AnnotationType } from "building-types/annotation/annotationTypesEnum";
import type { Building } from "building-types/building/buildingTypes";
import type {
  AnnotationHostWall,
  EntityHostedAnnotationPoint,
} from "building-types/entities/entityHostedAnnotationTypes";
import { WallHostedAnnotationSide } from "building-types/entities/entityHostedAnnotationTypes";
import type { RoomIndex } from "building-types/rooms/roomIndex";
import type { Room } from "building-types/rooms/roomTypes";
import type { WallMassSystem } from "building-types/walls/wallSytemTypes";
import type { RWTable, Table } from "common/data/table";
import type { WallLayerExtentsCache } from "doc/elements/openings/openingWallLayersTypes";
import { mat3Dot, mat3LineSegmentAffineTransform } from "geom/mat3";
import { polyLine2EdgeEval, polyLine2EdgeNormal, polyLine2ReverseCopy, polyLine2Shift } from "geom/polyLine2";
import type { Vec2 } from "geom/vec2";
import { vec2Scale, vec2Add } from "geom/vec2";
import { isCombinedGlobalInstanceMirror } from "../generationTools/generateOpeningsTools";
import { isEntityHostedAnnotationPoint } from "./entityHostedAnnotation";
import { getFromOrAddToWallLayerExtentsCache, getWallLayerOffset } from "./openings/openingWallLayers";

export type BuildingForEntityHostedAnnotations = Pick<Building, "rooms" | "roomIndex" | "wallMasses" | "mirror">;

export function resolveEntityHostedAnnotationTableArray(
  building: BuildingForEntityHostedAnnotations,
  annotations: Table<Annotation[]>,
  wallLayerExtentsCache: WallLayerExtentsCache = {},
): Table<Annotation[]> {
  const resolvedAnnotations: RWTable<Annotation[]> = {};
  for (const id in annotations) {
    resolvedAnnotations[id] = annotations[id].map((anno) =>
      resolveEntityHostedAnnotation(building, anno, wallLayerExtentsCache),
    );
  }

  return resolvedAnnotations;
}

export function resolveEntityHostedAnnotation(
  building: BuildingForEntityHostedAnnotations,
  anno: Annotation,
  wallLayerExtentsCache: WallLayerExtentsCache = {},
): Annotation {
  switch (anno?.type) {
    case AnnotationType.LINEAR_DIMENSION:
      const { entityHostedStart, entityHostedEnd } = anno;
      let { lineStartX, lineStartY, lineEndX, lineEndY } = anno;
      const { startX: startInitX, startY: startInitY, endX: endInitX, endY: endInitY } = anno;

      const [startX, startY] = resolveEntityHostedAnnotationPoint(
        building,
        entityHostedStart,
        anno.startX,
        anno.startY,
        wallLayerExtentsCache,
      );
      const [endX, endY] = resolveEntityHostedAnnotationPoint(
        building,
        entityHostedEnd,
        anno.endX,
        anno.endY,
        wallLayerExtentsCache,
      );

      const affineTransformMat = mat3LineSegmentAffineTransform(
        startInitX,
        startInitY,
        endInitX,
        endInitY,
        startX,
        startY,
        endX,
        endY,
        true,
        true,
      );

      const [relTransStartX, relTransStartY] = mat3Dot(
        affineTransformMat,
        lineStartX - startInitX,
        lineStartY - startInitY,
      );
      const [relTransEndX, relTransEndY] = mat3Dot(affineTransformMat, lineEndX - endInitX, lineEndY - endInitY);
      [lineStartX, lineStartY] = vec2Add(startX, startY, relTransStartX, relTransStartY);
      [lineEndX, lineEndY] = vec2Add(endX, endY, relTransEndX, relTransEndY);

      return {
        ...anno,
        startX,
        startY,
        endX,
        endY,
        lineStartX,
        lineStartY,
        lineEndX,
        lineEndY,
      };
    default:
      // TODO: handle other annotation types
      return anno;
  }
}

function resolveEntityHostedAnnotationPoint(
  building: BuildingForEntityHostedAnnotations,
  entityHostedPoint: EntityHostedAnnotationPoint | undefined,
  defaultX: number,
  defaultY: number,
  wallLayerExtentsCache: WallLayerExtentsCache = {},
): Vec2 {
  if (!isEntityHostedAnnotationPoint(entityHostedPoint)) {
    return [defaultX, defaultY];
  }

  const { rooms, roomIndex, wallMasses, mirror } = building;
  const { hostEntity } = entityHostedPoint;
  const { type, handle, edgeIndex, param, paramNormalized } = hostEntity;

  switch (type) {
    case "WALL": {
      const room = rooms[handle.id];

      if (!room) {
        return [defaultX, defaultY];
      }

      const { outline, level } = room;
      const wallMassSystem = wallMasses[level.id];
      if (!wallMassSystem) {
        return [defaultX, defaultY];
      }

      const effectiveMirror = isCombinedGlobalInstanceMirror(mirror, room);

      const resolvedOutline = effectiveMirror ? polyLine2Shift(polyLine2ReverseCopy(outline), 1) : outline;

      const [x, y] = polyLine2EdgeEval(resolvedOutline, edgeIndex, param, paramNormalized);
      const [nX, nY] = polyLine2EdgeNormal(resolvedOutline, edgeIndex);

      let offset = getWallHostedAnnotationAlignmentOffset(
        hostEntity,
        room,
        roomIndex,
        wallMassSystem,
        wallLayerExtentsCache,
      );

      if (effectiveMirror) {
        offset = -offset;
      }

      const [offsetX, offsetY] = vec2Scale(nX, nY, offset);

      return vec2Add(x, y, offsetX, offsetY);
    }
    default: {
      // TODO: handle other entity types
      return [defaultX, defaultY];
    }
  }
}

function getWallHostedAnnotationAlignmentOffset(
  hostWall: AnnotationHostWall,
  room: Room,
  roomIndex: RoomIndex,
  wallMassSystem: WallMassSystem,
  wallLayerExtentsCache: WallLayerExtentsCache = {},
): number {
  const { edgeIndex, wallSide, horizontalDatum } = hostWall;

  switch (wallSide) {
    case WallHostedAnnotationSide.OUTSIDE:
      const { outsideOffsetTable, mostOutwardLayerOutsideRoom } = getFromOrAddToWallLayerExtentsCache(
        room,
        edgeIndex,
        wallLayerExtentsCache,
        roomIndex,
        wallMassSystem,
      );
      return getWallLayerOffset(outsideOffsetTable, mostOutwardLayerOutsideRoom, horizontalDatum);
    case WallHostedAnnotationSide.INSIDE:
      const { insideOffsetTable, mostInwardLayerIntoRoom } = getFromOrAddToWallLayerExtentsCache(
        room,
        edgeIndex,
        wallLayerExtentsCache,
        roomIndex,
        wallMassSystem,
      );
      return -getWallLayerOffset(insideOffsetTable, mostInwardLayerIntoRoom, horizontalDatum);
    default:
      return 0;
  }
}
